import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { StandardPolicy, PolicyLabel, PolicyScope } from '@system-config/policy-management/domain';
import { CREATE_STANDARD_POLICY, GET_STANDARD_POLICIES } from '@system-config/policy-management/graphql';
import { useState, useCallback } from 'react';

interface CreateStandardPolicyInput {
  name: string;
  description?: string;
  isEnabled: boolean;
  scope: PolicyScope;
  sourcePolicyId?: string;
  labels: PolicyLabel[];
}

interface CreateStandardPolicyResponse {
  createStandardPolicy: StandardPolicy;
}

export function useCreateStandardPolicy() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [createStandardPolicyMutation, { loading }] = useMutation<CreateStandardPolicyResponse>(CREATE_STANDARD_POLICY);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createStandardPolicy = async (input: CreateStandardPolicyInput): Promise<boolean> => {
    try {
      track('CreateStandardPolicyInitiated', {
        policyName: input.name,
        scope: input.scope,
        labelsCount: input.labels.length.toString(),
      });

      await createStandardPolicyMutation({
        variables: { input },
        refetchQueries: [{ query: GET_STANDARD_POLICIES }],
        awaitRefetchQueries: true,
      });

      track('CreateStandardPolicySucceeded', {
        policyName: input.name,
        scope: input.scope,
      });

      return true;
    } catch (error) {
      track('CreateStandardPolicyFailed', {
        policyName: input.name,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    createStandardPolicy,
    loading,
    errors,
    resetErrors,
  };
}

export default useCreateStandardPolicy;
